import React, { useMemo } from 'react';
import { Badge, IconButton, HStack, Tooltip, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { PencilSimple, Trash } from 'phosphor-react';
import { DataTable } from '../../../../../../components/datatable';
import { IAcumulador } from '../../../../../../types/icms';
import { IPagination } from '../../../../../../types/shared';

interface AcumuladoresTableProps {
  data: IAcumulador[];
  pagination: IPagination;
  isFetching?: boolean;
  onEdit: (row: IAcumulador) => void;
  onDelete: (row: IAcumulador) => void;
}

const classificacaoColor: Record<string, string> = {
  difal: 'orange',
  st: 'purple',
  simples: 'blue',
  isenta: 'gray',
  nao_tributada: 'gray',
  credito_outros: 'cyan',
  servico: 'teal',
  outras: 'gray',
};

export function AcumuladoresTable({
  data,
  pagination,
  isFetching,
  onEdit,
  onDelete,
}: AcumuladoresTableProps) {
  const columns = useMemo<ColumnDef<IAcumulador>[]>(
    () => [
      {
        header: 'Código',
        accessorKey: 'codi_acu',
      },
      {
        header: 'Nome',
        accessorKey: 'nome_acu',
        cell: info => (
          <Text fontSize="12px" textAlign="left">
            {(info.getValue() as string) || '—'}
          </Text>
        ),
      },
      {
        header: 'Operação',
        accessorKey: 'tipo_operacao',
        cell: info => {
          const v = info.getValue() as string;
          return (
            <Badge colorScheme={v === 'entrada' ? 'blue' : 'orange'} fontSize="9px">
              {v}
            </Badge>
          );
        },
      },
      {
        header: 'Classificação',
        accessorKey: 'classificacao',
        cell: info => {
          const v = info.getValue() as string;
          return (
            <Badge colorScheme={classificacaoColor[v] ?? 'gray'} fontSize="9px">
              {v}
            </Badge>
          );
        },
      },
      {
        header: 'CFOPs',
        accessorKey: 'cfops_padrao',
        cell: info => {
          const v = info.getValue() as string[] | null | undefined;
          if (!v || v.length === 0) return '—';
          return (
            <Text fontSize="11px" color="gray.600">
              {v.join(', ')}
            </Text>
          );
        },
      },
      {
        header: 'Crédito / Débito',
        accessorKey: 'gera_credito',
        cell: info => {
          const row = info.row.original;
          return (
            <HStack spacing={1} justify="center">
              {row.gera_credito && <Badge colorScheme="green">CR</Badge>}
              {row.gera_debito && <Badge colorScheme="red">DB</Badge>}
              {!row.gera_credito && !row.gera_debito && <Text fontSize="11px">—</Text>}
            </HStack>
          );
        },
      },
      {
        header: 'Ações',
        id: 'actions',
        cell: info => {
          const row = info.row.original;
          return (
            <HStack justify="center" spacing={1}>
              <Tooltip label="Editar">
                <IconButton
                  aria-label="Editar"
                  icon={<PencilSimple size={14} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => onEdit(row)}
                />
              </Tooltip>
              <Tooltip label="Remover">
                <IconButton
                  aria-label="Remover"
                  icon={<Trash size={14} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onDelete(row)}
                />
              </Tooltip>
            </HStack>
          );
        },
      },
    ],
    [onEdit, onDelete],
  );

  return (
    <DataTable<IAcumulador>
      columns={columns}
      data={data}
      pagination={pagination}
      isFetching={isFetching}
      emptyMsg="Nenhum acumulador classificado. Use 'Descobrir acumuladores' para começar."
      loadingMsg="Carregando acumuladores..."
    />
  );
}
